/**  MODIFIED COPY OF src/common-app/state/modules/engines/transcription/saga.ts TODO find way to reuse common code! */

import {
  all,
  call,
  cancel,
  fork,
  put,
  race,
  select,
  takeEvery,
  take,
  delay,
} from 'typed-redux-saga/macro';
import { enqueueSnackbar } from '@common-modules/snackbar';
import { OUT_CREATE_JOB_FAIL, OUT_CREATE_JOB_SUCCESS } from '@worker';

import { STOP_ALL_ENGINE_POLLING } from '../actions';
import * as Actions from './actions';
import { selectEngineStatus } from './selectors';
import {
  cancelJobService,
  checkJobStatusService,
  checkTaskStatusService,
  createEngineJobService,
} from './services';
import { TDOId } from '../../universal/models/Brands';
import { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import { sagaIntl } from '@common/i18n';

export function* saga() {
  yield* all([
    fork(onProcessEngineRequestAction),
    fork(onProcessEngineRequestSuccessAction),
    fork(onProcessEngineRequestFailureAction),
    fork(onCreateEngineJobAction),
    fork(onStartPollingEngineResultsAction),
    fork(onCheckJobStatusAction),
    fork(onCheckTaskStatusAction),
    // fork(onQueryEngineResultsRecordAction),
    fork(onCancelJobAction),
    fork(onCancelJobSuccessAction),
    fork(onCancelJobFailureAction),
  ]);
}

/**
 * Action watchers.
 */

function* onProcessEngineRequestAction() {
  yield* takeEvery(Actions.PROCESS_ENGINE_REQUEST, function* (action) {
    yield* startEngineJob(action.payload);
  });
}

export function* onProcessEngineRequestSuccessAction() {
  yield* takeEvery(
    Actions.PROCESS_ENGINE_REQUEST_SUCCESS,
    onHandleEngineRequestSuccessAction
  );
}

export function* onHandleEngineRequestSuccessAction() {
  const intl = sagaIntl();
  yield* put(
    enqueueSnackbar({
      autoHideDuration: 10_000,
      message: intl.formatMessage({
        id: 'processVoiceMaskSuccess',
        defaultMessage: 'Voice mask audio is now ready.',
      }),
    })
  );
}

function* onProcessEngineRequestFailureAction() {
  yield* takeEvery(Actions.PROCESS_ENGINE_REQUEST_FAILURE, function* (action) {
    yield* put(
      enqueueSnackbar({
        message: action.payload.errorMsg,
      })
    );
  });
}

function* onCreateEngineJobAction() {
  yield* takeEvery(Actions.CREATE_ENGINE_JOB, function* (action) {
    yield* put(createEngineJobService(action.payload));
  });
}

function* onStartPollingEngineResultsAction() {
  yield* takeEvery(Actions.START_POLLING_ENGINE_RESULTS, function* (action) {
    yield* call(startPollingJobStatus, action.payload);
  });
}

function* onCheckJobStatusAction() {
  yield* takeEvery(Actions.CHECK_JOB_STATUS, function* (action) {
    yield* put(checkJobStatusService(action.payload));
  });
}

function* onCheckTaskStatusAction() {
  yield* takeEvery(Actions.CHECK_TASK_STATUS, function* (action) {
    yield* put(checkTaskStatusService(action.payload));
  });
}

function* onCancelJobAction() {
  yield* takeEvery(Actions.CANCEL_JOB, function* (action) {
    yield* put(cancelJobService(action.payload));
  });
}

function* onCancelJobSuccessAction() {
  yield* takeEvery(Actions.CANCEL_JOB_SUCCESS, function* () {
    yield* put(
      enqueueSnackbar({
        message: 'The voice mask process has been canceled.',
      })
    );
  });
}

function* onCancelJobFailureAction() {
  yield* takeEvery(Actions.CANCEL_JOB_FAILURE, function* () {
    yield* put(
      enqueueSnackbar({
        message: "I'm sorry. The cancel request failed.",
      })
    );
  });
}

/**
 * Start engine job process.
 */

export const startEngineJob = function* ({
  tdoId,
  tdoName,
  payload,
  retryCount = 3,
}: {
  tdoId: TDOId;
  tdoName: string;
  payload: {
    targetId: string;
  };
  retryCount?: number;
}): any {
  yield* put(Actions.createEngineJobAction({ tdoId, tdoName, payload }));
  const { ok, err } = yield* race({
    ok: take(Actions.CREATE_ENGINE_JOB_SUCCESS),
    err: take(Actions.CREATE_ENGINE_JOB_FAILURE),
  });

  if (err && retryCount <= 0) {
    yield* put(OUT_CREATE_JOB_FAIL(err));
    yield* cancel();
  }

  if (err && retryCount > 0) {
    yield* delay(3000);
    yield* startEngineJob({
      tdoId,
      tdoName,
      payload,
      retryCount: retryCount - 1,
    });
  }

  if (ok) {
    yield* put(
      Actions.startPollingEngineResultsAction({
        tdoId,
        jobId: ok.payload.createJob.id,
      })
    );
    yield* put(OUT_CREATE_JOB_SUCCESS(ok));
  }
};

export function* startPollingJobStatus({
  jobId,
  taskId,
  tdoId,
}: {
  jobId: string;
  taskId?: string;
  tdoId: TDOId;
}) {
  while (true) {
    const { isRunning, status, error } = yield* select(
      selectEngineStatus(tdoId, jobId)
    );

    if (status === 'complete') {
      // we could just put snackbar message here instead
      break;
    }
    if (!isRunning || error) {
      yield* put(Actions.stopPollingEngineResultsAction({ tdoId, jobId }));
      yield* cancel();
    }
    // if taskId available use task status in place of job status since if run during upload with other engines don't want to wait on other engines to complete job
    if (taskId) {
      yield* put(Actions.checkTaskStatusAction({ tdoId, taskId, jobId }));
    } else {
      yield* put(Actions.checkJobStatusAction({ tdoId, jobId }));
    }
    const { stop } = yield* race({
      ok: delay(10_000),
      stop: take([
        isActionOfTDO(Actions.STOP_POLLING_ENGINE_RESULTS, jobId),
        STOP_ALL_ENGINE_POLLING,
      ]),
    });
    if (stop) {
      yield* put(Actions.stopPollingEngineResultsAction({ tdoId, jobId }));
      yield* cancel();
      return;
    }
  }

  yield* put(Actions.processEngineRequestSuccessAction({ tdoId, jobId }));
}

export const isActionOfTDO =
  <P>(
    action: ActionCreatorWithPayload<P extends { jobId: string } ? P : never>,
    jobId: string
  ) =>
  ({ type, payload }: { type: string; payload?: { jobId?: string } }) =>
    type === action.type && payload?.jobId === jobId;
